// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file 
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { App } from "../../app/App.js"
import { createLucideIcon, fadeElementIn, fadeElementOut, renderLucideIcons } from "../../utils/index.js"

// @@body
class MeasurementInput extends HTMLElement {
    #input = document.createElement("input")
    #units = /** @type {HTMLDivElement[]} */ ([])
    #min = parseFloat(this.dataset.min || "") || - Infinity
    #max = parseFloat(this.dataset.max || "") || Infinity
    #selectedUnit = document.createElement("div")
    #listener = (/** @type {Event} */ event) => {
        event.stopPropagation()
        if (event.target instanceof HTMLInputElement) {
            const { value, dataset } = event.target
            event.target.value = parseFloat(value)
                .toFixed(parseInt(dataset.fixedPrecision || "0"))
            this.#updateInternalInput()
        }
    }

    /**
     * 
     */
    constructor() {
        super()

        this.querySelectorAll("div.unit")
            .forEach(unit => {
                if (!(unit instanceof HTMLDivElement)) { return }

                if (unit.ariaSelected) {
                    this.#selectedUnit = unit
                    unit.ariaSelected = null
                }
                else { unit.classList.add("closed") }
                this.#units.push(unit)
            })
    }

    /**
     * 
     */
    connectedCallback() {
        // Fetch hidden select element attributes from custom component dataset
        // attributes.
        this.#input.id = this.dataset.id || ""
        this.#input.name = this.dataset.name || ""
        this.#input.hidden = true

        // Mount hidden input element which will hold the state of custom
        // multi unit input.
        this.append(this.#input)

        // Add label element if required data attribute is provided.
        if (this.dataset.label) {
            const label = document.createElement("label")
            label.htmlFor = this.#input.id
            label.innerHTML = this.dataset.label
            this.insertBefore(label, this.firstChild)
        }

        this.#addUnitInputEventListener()

        this.querySelectorAll("input").forEach(input => {
            input.addEventListener("focusin", () => {
                input.select()
                fadeElementIn(this.tooltip)
            })

            input.addEventListener("focusout", () => {
                fadeElementOut(this.tooltip)
                if (!document.body.classList.contains("hoverable")) {
                    const message = {
                        action: "MOBILE_MEASUREMENT_INPUT_CLOSED"
                    }
                    window.parent.postMessage(message, "*")
                }
            })
        })

        renderLucideIcons()

        this.querySelectorAll(".__unit-switch")
            .forEach(element => {
                element.addEventListener("click", () => {
                    this.switchUnits()
                    // protect this in a callback instead (?) - this should not be
                    // in generic function
                    App.instance.state.deck.renderer.flags.metric = !App.instance.state.deck.renderer?.flags.metric
                })
            })
    }

    switchUnits() {
        this.#removeUnitInputEventListener()

        const index = this.#units.indexOf(this.#selectedUnit) + 1
        this.#selectedUnit = this.#units[index % this.#units.length]
        for (const unit of this.#units) {
            unit === this.#selectedUnit
                ? unit.classList.remove("closed")
                : unit.classList.add("closed")
        }
        this.updateSelectedUnitInputs()

        this.#addUnitInputEventListener()
    }

    updateSelectedUnitInputs() {
        let remainingMeasurement = this.value
        const inputs = this.#selectedInputs
        for (const [index, input] of inputs.entries()) {
            const { dataset } = input
            const conversionFactor = parseFloat(dataset.conversionFactor || "0")

            const qty = index === inputs.length - 1
                ? remainingMeasurement / conversionFactor
                : Math.floor(remainingMeasurement / conversionFactor)
            input.value = qty.toFixed(parseInt(dataset.fixedPrecision || "0"))

            remainingMeasurement -= qty * conversionFactor
        }
    }

    #updateInternalInput() {
        let measurement = 0
        for (const { value, dataset } of this.#selectedInputs) {
            const conversionFactor = parseFloat(dataset.conversionFactor || "0")
            measurement += parseFloat(value || "0") * conversionFactor
        }

        if (measurement < this.#min || measurement > this.#max) {
            alert(`Please enter a length between ${this.#min / 100}m and ${this.#max / 100}m.`)
            this.updateSelectedUnitInputs()
            // this.value = measurement < this.#min ? this.#min : this.#max
            // this.updateSelectedUnitInputs()
            return
        }

        const fractionDigits = parseInt(this.dataset.fixedPrecision || "0")
        this.value = parseFloat(measurement.toFixed(fractionDigits))
    }

    #addUnitInputEventListener() {
        for (const input of this.#selectedInputs) {
            input.addEventListener("change", this.#listener)
        }
    }

    #removeUnitInputEventListener() {
        for (const input of this.#selectedInputs) {
            input.removeEventListener("change", this.#listener)
        }
    }

    get #selectedInputs() {
        const inputs = /** @type {HTMLInputElement[]} */ ([])
        this.#selectedUnit
            .querySelectorAll("div.labeled-input input")
            .forEach(input => {
                if (!(input instanceof HTMLInputElement)) { return }
                inputs.push(input)
            })

        return inputs
    }

    /**
     * 
     */
    get value() {
        const { round } = this.dataset
        const value = parseFloat(this.#input.value)
        return round === "normal" ? Math.round(value)
            : round === "ceil" ? Math.ceil(value)
            : round === "floor" ? Math.floor(value)
            : value
    }

    /**
     * 
     */
    set value(value) {
        this.#input.value = `${value}`
        const event = new Event("change")
        this.dispatchEvent(event)
    }

    get isFocused() {
        for (const input of this.#selectedInputs) {
            if (document.activeElement === input) { return true }
        }

        return false
    }

    get tooltip() {
        return /** @type {HTMLDivElement} */ (this.querySelector("div.tooltip"))
    }

    static get customName() {
        return "measurement-input"
    }
}

// @@exports
export { MeasurementInput }
